import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'store';
import qs from 'query-string';
import { isEqual } from 'lodash';
import { mapValues, keyBy } from 'lodash';
import { useLoadData } from 'plugins/api/endpoints/useLoadData';
import { loadDataUrlParser } from 'plugins/util/parser';
import { usePrevious } from 'hooks';
import { reduxActions } from 'store';

import { IWiseTableQueryParams, IWiseTableData, IWiseTableParameter, IWiseTableFilter } from 'components/wise/types';
import { IWiseTableAdvancedFilter } from 'components/wise/types';
import { ILoadDataParam } from 'plugins/api/types';
import { useWiseTableReduxSync } from 'components/wise/WiseTable/hooks/useWiseTableReduxSync';
import { advancedFiltersToParams } from 'components/wise/WiseTable/utils/advancedFiltersToParams';

interface IUseWiseTableDataParams {
  loadDataParams?: ILoadDataParam;
  requestParams?: object;
  pageSymbol?: string;
  componentSymbol: string;
  parameters?: IWiseTableParameter[];
  filters?: IWiseTableFilter[];
  defaultLimit: number;
}

interface IUseWiseTableDataReturn {
  tableData: IWiseTableData | undefined;
  queryParams: IWiseTableQueryParams;
  setQueryParams: React.Dispatch<React.SetStateAction<IWiseTableQueryParams>>;
  refetchTableData: () => void;
  isLoading: boolean;
  handlePageChange: (page: number) => void;
  handleLimitChange: (limit: number) => void;
  handleSortChange: (sortBy: string | null, sortDirection: 'asc' | 'desc' | null) => void;
  // Eksponowane do eksportu CSV - umozliwia powtorne wywolanie tego samego endpointu
  // z pelnym limitem (= total_count) zachowujac aktualnie aktywne filtry.
  loadDataUrl: string;
  effectiveQueryParams: Record<string, unknown>;
}

/**
 * Hook do zarządzania danymi tabeli
 */
export const  useWiseTableData = ({
  loadDataParams,
  requestParams,
  pageSymbol,
  componentSymbol,
  parameters,
  filters = [],
  defaultLimit
}: IUseWiseTableDataParams): IUseWiseTableDataReturn => {
  const dispatch = useDispatch();
  const { search } = useLocation();

  // Hook do synchronizacji z Redux
  const { syncToRedux, initialParams, isDynamicUI } = useWiseTableReduxSync({
    pageSymbol,
    componentSymbol
  });

  // Zaawansowane filtry z Redux – do budowania query (po „Zastosuj” / filtrach z kolumny)
  const savedFiltersFromRedux = useSelector((state) =>
    isDynamicUI && pageSymbol
      ? state.dynamicPage?.wiseTableAdvancedFilters?.[pageSymbol]?.[componentSymbol]
      : state.traderPanel?.wiseTableAdvancedFilters?.[componentSymbol]
  ) as IWiseTableAdvancedFilter[] | undefined;
  const savedFilters = savedFiltersFromRedux ?? [];

  // Parsowanie URL z loadDataParams
  const loadDataUrl = loadDataUrlParser(loadDataParams?.get_url || '', {
    ...requestParams,
    ...qs.parse(search)
  });

  const currentGetUrl = loadDataParams?.get_url;
  const prevGetUrl = usePrevious(loadDataParams?.get_url);
  const prevLoadDataUrl = usePrevious(loadDataUrl);

  // Helper: merge URL params (only known parameter keys) over base params.
  // Dla parametrow typu 'date_range'/'range' WiseRangeButton wysyla {field}_from, {field}_to oraz 'period'
  // - dopuszczamy te warianty w URL, zeby dashboard mogl preselectowac zakres przez ?date_from=...&date_to=...
  const paramSymbols = parameters
    ? new Set(
        parameters.flatMap((p) => {
          const symbol = p.field_symbol;
          if (!symbol) return [] as string[];
          if (p.type === 'date_range' || p.type === 'range') {
            return [symbol, `${symbol}_from`, `${symbol}_to`, 'period'];
          }
          return [symbol];
        })
      )
    : new Set<string>();
  const mergeUrlParamsInto = (base: IWiseTableQueryParams, searchString: string): IWiseTableQueryParams => {
    const urlParams = qs.parse(searchString, { parseBooleans: false, parseNumbers: false }) as Record<string, string>;
    const merged = { ...base };
    let hasUrlFilterChange = false;
    Object.keys(urlParams).forEach((key) => {
      if (paramSymbols.has(key) && urlParams[key] !== undefined && urlParams[key] !== '') {
        merged[key] = urlParams[key];
        hasUrlFilterChange = true;
      }
    });
    // Przy zmianie filtrów z URL (np. client_id, due_date) reset strony na 1
    if (hasUrlFilterChange) {
      merged.page = 1;
    }
    return merged;
  };

  // Inicjalizacja queryParams: baza z Redux lub default_value, potem zawsze nadpisane przez URL
  const [queryParams, setQueryParamsState] = useState<IWiseTableQueryParams>(() => {
    const baseParams: IWiseTableQueryParams = initialParams ?? {
      page: 1,
      limit: defaultLimit,
      ...(parameters ? mapValues(keyBy(parameters, 'field_symbol'), 'default_value') : {})
    };
    return mergeUrlParamsInto(baseParams, search);
  });

  // Przy zmianie URL (np. ponowne wejście z tile) – zastosuj parametry z query
  useEffect(() => {
    setQueryParamsState((prev) => mergeUrlParamsInto(prev, search));
  }, [search]);

  // Wrapper dla setQueryParams
  const setQueryParams: React.Dispatch<React.SetStateAction<IWiseTableQueryParams>> = useCallback(
    (updater) => {
      setQueryParamsState((prevState) => {
        return typeof updater === 'function' ? updater(prevState) : updater;
      });
    },
    []
  );

  // Parametry wysyłane do API: queryParams + parametry z zaawansowanych filtrów (Redux)
  const effectiveQueryParams = useMemo(
    () => ({ ...queryParams, ...advancedFiltersToParams(savedFilters, filters) }),
    [queryParams, savedFilters, filters]
  );

  // Pobieranie danych z API
  const {
    data: tableDataResponse,
    isLoading,
    refetch: refetchTableData
  } = useLoadData(loadDataUrl, effectiveQueryParams, {
    onSuccess: (data) => {
      if (isDynamicUI && pageSymbol) {
        dispatch(reduxActions.setContextData({ [pageSymbol]: { [componentSymbol]: data } }));
      }
    },
    enabled: false,
    cacheTime: 1,
    keepPreviousData: false
  });

  // Transformacja danych z API do formatu IWiseTableData (useMemo zapobiega nowej referencji przy każdym renderze → unika infinite loop w rodzicu przy onDataLoad)
  const tableData: IWiseTableData | undefined = useMemo(
    () =>
      tableDataResponse
        ? {
            items: tableDataResponse.items || [],
            total_count: tableDataResponse.total_count || 0,
            total_pages: tableDataResponse.total_pages || 0,
            page: queryParams.page ?? 1,
            limit: queryParams.limit ?? defaultLimit
          }
        : undefined,
    [tableDataResponse, queryParams.page, queryParams.limit, defaultLimit]
  );

  // useRef aby uniknąć zapisu przy pierwszym renderze (inicjalizacja)
  const isInitialMount = useRef(true);
  const prevQueryParamsRef = useRef<IWiseTableQueryParams | null>(null);
  
  useEffect(() => {
    // Pomijamy zapis przy pierwszym renderze - wartości są już w Redux lub będą zainicjalizowane
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevQueryParamsRef.current = queryParams;
      return;
    }

    if (prevQueryParamsRef.current && isEqual(prevQueryParamsRef.current, queryParams)) {
      return;
    }

    prevQueryParamsRef.current = queryParams;

    syncToRedux(queryParams);
  }, [queryParams, syncToRedux]);

  // Przy zmianie zaawansowanych filtrów (Redux) – reset strony na 1
  const prevSavedFiltersRef = useRef<IWiseTableAdvancedFilter[]>(savedFilters);
  useEffect(() => {
    if (!isEqual(prevSavedFiltersRef.current, savedFilters)) {
      prevSavedFiltersRef.current = savedFilters;
      setQueryParamsState((prev) => ({ ...prev, page: 1 }));
    }
  }, [savedFilters]);

  // Automatyczne odświeżanie danych przy zmianie queryParams, filtrów lub URL
  const prevEffectiveQueryParams = usePrevious(effectiveQueryParams);
  useEffect(() => {
    const urlChanged = loadDataUrl && loadDataUrl !== prevLoadDataUrl;
    const getUrlOrQueryChanged =
      !isEqual(currentGetUrl, prevGetUrl) ||
      !isEqual(effectiveQueryParams, prevEffectiveQueryParams);
    if (loadDataUrl && (urlChanged || getUrlOrQueryChanged)) {
      refetchTableData();
    }
  }, [
    currentGetUrl,
    prevGetUrl,
    effectiveQueryParams,
    prevEffectiveQueryParams,
    loadDataUrl,
    prevLoadDataUrl,
    refetchTableData
  ]);

  // Obsługa zmiany strony
  const handlePageChange = (page: number) => {
    setQueryParams((prevState) => ({
      ...prevState,
      page
    }));
  };

  // Obsługa zmiany limit
  const handleLimitChange = (limit: number) => {
    setQueryParams((prevState) => ({
      ...prevState,
      limit,
      page: 1
    }));
  };

  // Obsługa zmiany sortowania
  const handleSortChange = (sortBy: string | null, sortDirection: 'asc' | 'desc' | null) => {
    setQueryParams((prevState) => {
      const newParams = { ...prevState };

      if (sortBy && sortDirection) {
        newParams.sortBy = sortBy;
        newParams.sortDirection = sortDirection;
      } else {
        delete newParams.sortBy;
        delete newParams.sortDirection;
      }

      newParams.page = 1;

      return newParams;
    });
  };

  return {
    tableData,
    queryParams,
    setQueryParams,
    refetchTableData,
    isLoading,
    handlePageChange,
    handleLimitChange,
    handleSortChange,
    // Eksponowane do eksportu CSV - umozliwia powtorne wywolanie tego samego endpointu
    // z pelnym limitem (= total_count) zachowujac aktualnie aktywne filtry.
    loadDataUrl,
    effectiveQueryParams
  };
};
